import React from "react";
import { Box, Chip, IconButton, Paper, Stack, Typography } from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const ShiftPositionItem = (row, idx, positionHandler, removePosition, assignedStaff = []) => {
  const theme = useTheme();

  if (!row) return <></>;
  const { positionName, numberPositions, billRate, payRate } = row;

  // Calculate available positions
  const totalPositions = parseInt(numberPositions, 10) || 0;
  const assignedCount = assignedStaff.filter(
    (staff) => staff.assignedPosition === positionName
  ).length;
  const availableCount = totalPositions - assignedCount;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 1,
        cursor: "pointer",
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          elevation: 2,
          backgroundColor: theme.palette.action.hover,
          transform: "translateY(-1px)",
        },
      }}
      onClick={() => positionHandler(row, true, idx)}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {/* Left Section - Position Info */}
        <Box flex={1}>
          <Stack direction="row" spacing={2} alignItems="center" mb={1}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: theme.palette.text.primary,
              }}
            >
              {positionName}
            </Typography>
            <Chip
              label={`Total: ${numberPositions}`}
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.75rem",
                height: 24,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Available: ${availableCount}`}
              size="small"
              sx={{
                fontSize: "0.75rem",
                height: 24,
                backgroundColor: availableCount > 0 ? "#e8f5e8" : "#ffebee",
                color: availableCount > 0 ? "#2e7d32" : "#c62828",
                fontWeight: 600,
              }}
            />
            <Chip
              label={`Assigned: ${assignedCount}`}
              size="small"
              sx={{
                fontSize: "0.75rem",
                height: 24,
                backgroundColor: "#f3f4f6",
                color: "#374151",
              }}
            />
          </Stack>
        </Box>

        {/* Center Section - Rates */}
        <Box sx={{ minWidth: 160, textAlign: "center" }}>
          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.85rem", color: theme.palette.text.secondary }}
            >
              Pay Rate
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#2e7d32" }}
            >
              ${payRate}
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ minWidth: 160, textAlign: "center" }}>
          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.85rem", color: theme.palette.text.secondary }}
            >
              Bill Rate
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#1976d2" }}
            >
              ${billRate}
            </Typography>
          </Stack>
        </Box>

        {/* Right Section - Actions */}
        <Box>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                positionHandler(row, true, idx);
              }}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: `${theme.palette.primary.light}20`,
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                removePosition(idx);
              }}
              sx={{
                color: theme.palette.error.main,
                "&:hover": {
                  backgroundColor: `${theme.palette.error.light}20`,
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};

export default ShiftPositionItem;
